import React, { useEffect, useState } from "react";
import {
  DocumentPartTree,
  DocumentPartTreeSelection,
  getWorkflowStatus,
  TreeViewDataItem,
} from "../cpm/DocumentPartTree";
import { formatMessage, formatSehorseLastUpdateDate } from "../../utils/helpers/LanguageHelper";
import { FlatPathTreeItem } from "../generic/FlatPathTreeExplorer";
import { openDocumentById } from "../../utils/helpers/OpenDocumentHelper";
import { ActionButton, IContextualMenuProps, IIconProps, ImageFit } from "@fluentui/react";
import {
  fetchAllDocumentPartStatus,
  fetchAzureDefaultSiteFromExternalConfig,
  fetchDocumentPartStatus,
} from "../../api/document/DocumentsPart";
import { isNotAuthorizedUser } from "../../utils/user-function/userFunctionUtils";
import { UserFunctionEnum } from "../../api/models/user-function-enum";
import { UserFunction } from "../../api/models/user-function";
import { useAppSelector } from "../../redux/hooks/hooks";
import {
  selectIxbrlSettings,
  selectSeahorseDocumentLinked,
  selectServiceProvider,
  selectUserFunctions,
  setSeahorseDocumentLinked,
} from "../../redux/feature/settings/configurations-slice";
import { DocumentPosition } from "../../api/models/document-position";
import { fetchInitialDocumentPartTreeProps } from "../cpm/DocumentPartTreeHelper";
import { ServiceProvider } from "../../api/models/service-provider";
import {
  fetchSeahorseDocumentLinked,
  hasNoDefaultServiceProvider,
  hasNoIxbrlSetting,
  sendDocumentToIXBRL,
} from "../../pages/seahorse/SeahorseUtils";
import { SeahorseDocumentLinked } from "../../api/models/seahorse-document-linked";
import { store } from "../../redux/store/store";
import { DocumentRoot } from "../../api/models/document-root";
import { DocumentPart } from "../../api/models";
import { DocumentPartWithStatus } from "../../api/models/document-part-with-status";
import { IxbrlSetting } from "../../api/models/ixbrl-setting";

const getIconButtonMenuProps = (
  itemSelected: TreeViewDataItem,
  itemHierarchicalIndex: string,
  userFunctions: UserFunction[],
  defaultServiceProvider: ServiceProvider,
  seahorseDocumentLinked: SeahorseDocumentLinked[],
  allIxbrlSettings: IxbrlSetting[],
  idPartRoot: string,
  rootId: string,
  onDeleteDocument?: (item: FlatPathTreeItem) => void,
  onExportDocument?: (item: FlatPathTreeItem) => void,
  documentPartWithStatus?: DocumentPartWithStatus[]
) => {
  let notAuthorizedUserForDelete = isNotAuthorizedUser(
    UserFunctionEnum.MENU_OUTLINE_PANEL_DELETE_DOCUMENT_PART,
    userFunctions
  );
  let notAuthorizedUserForSendCopyIXBRL = isNotAuthorizedUser(UserFunctionEnum.MENU_IXBRL_USER, userFunctions);
  const SUBMITTED: string = "50";
  const items: any[] = [];

  /*
    Pulsante spostato nella library fin quando non vengono risolti gli errori nella pagina "Outline" in caso di documenti con moltissime docparts.
    Dopo la risoluzione, va rimosso dalla library e riattivato qui.
   */
  // items.push({
  //   id: "download-copy",
  //   key: "download-copy",
  //   text: formatMessage("cof.base.downloadacopy"),
  //   onClick: () => {
  //     store.dispatch(downloadDocumentCopy({ document: itemSelected }));
  //     store.dispatch(navigate(DOWNLOAD_COPY));
  //   },
  //   hidden: itemSelected.key != rootId,
  //   disabled: false,
  // });
  if (onDeleteDocument) {
    items.push({
      key: "delete",
      text: formatMessage("cof.base.delete"),
      onClick: () => {
        onDeleteDocument(itemSelected);
      },
      hidden: !(itemSelected.key != idPartRoot && (itemHierarchicalIndex.match(/_/g)?.length ?? 0) < 1),
      disabled:
        notAuthorizedUserForDelete ||
        (documentPartWithStatus?.length > 0 && haveSubmittedStatus(itemSelected, documentPartWithStatus)),
    });
  }
  if (onExportDocument) {
    items.push({
      key: "export",
      text: formatMessage("cof.base.export"),
      onClick: () => {
        onExportDocument(itemSelected);
      },
    });
  }
  if (!notAuthorizedUserForSendCopyIXBRL) {
    const selected = itemSelected as TreeViewDataItem;
    items.push({
      id: "sendcopyixbrl_menuitem",
      key: "sendcopyixbrl",
      text: formatMessage("cof.base.sendcopyixbrl"),
      onClick: () => {
        sendDocumentToIXBRL(selected.data, defaultServiceProvider.id).then(() => {
          fetchSeahorseDocumentLinked().then((result) => store.dispatch(setSeahorseDocumentLinked(result)));
        });
      },
      hidden: selected.key != rootId || hasNoIxbrlSetting(rootId, allIxbrlSettings),
      disabled: hasNoDefaultServiceProvider(defaultServiceProvider),
    });
    items.push({
      key: "updated",
      text: formatSehorseLastUpdateDate(seahorseDocumentLinked, selected.data.driveItemId),
      hidden: formatSehorseLastUpdateDate(seahorseDocumentLinked, selected.data.driveItemId).length <= 0,
      disabled: "true",
      className: "sentDateUpdate",
    });
  }

  function haveSubmittedStatus(selectedItem: FlatPathTreeItem, documentPartWithStatus: DocumentPartWithStatus[]) {
    return getWorkflowStatus(selectedItem.key, documentPartWithStatus)?.workflowStatusCode === SUBMITTED;
  }

  return {
    items,
    directionalHintFixed: true,
    hidden: !(itemSelected.key != idPartRoot && (itemHierarchicalIndex.match(/_/g)?.length ?? 0) < 2),
  };
};

export const DocumentOutline = ({
  isOutlineRibbon,
  isSourcesRibbon,
  isLibraryRibbon,
  isOpenOutlineRibbon,
  selectedTreeItem,
  onDeleteDocument,
  onSetSelectedItems,
  onExportDocument,
  onItemClick,
  idPartRoot,
  isLinkExisting,
  updateTreeItems,
  getIconButtonOutlineRibbon,
  changePage,
  parents,
  ignoreFetchPath,
  documentPartWithStatus,
  setDocumentPartWithStatus,
}: {
  isOutlineRibbon?: boolean;
  isSourcesRibbon?: boolean;
  isLibraryRibbon?: boolean;
  isOpenOutlineRibbon?: boolean;
  selectedTreeItem: FlatPathTreeItem;
  onDeleteDocument?: (item: FlatPathTreeItem) => void;
  onSetSelectedItems?: (item: TreeViewDataItem) => void;
  onExportDocument?: (item: FlatPathTreeItem) => void;
  onItemClick?: (selection: DocumentPartTreeSelection) => void;
  idPartRoot?: string;
  isLinkExisting?: boolean;
  updateTreeItems?: (treeItems: any, modifiedFields: boolean, showButton: boolean) => void;
  getIconButtonOutlineRibbon?: (
    item: any,
    itemHierarchicalIndex: string,
    documentPartWithStatus: DocumentPartWithStatus[]
  ) => IContextualMenuProps;
  changePage?: (page: string) => void;
  parents?: DocumentPosition[];
  ignoreFetchPath?: boolean;
  documentPartWithStatus?: DocumentPartWithStatus[];
  setDocumentPartWithStatus?: (
    documentPartWithStatus: (prevState: DocumentPartWithStatus[]) => DocumentPartWithStatus[]
  ) => void;
}) => {
  const [treeItems, setTreeItems] = useState<TreeViewDataItem[]>([
    {
      text: selectedTreeItem.document.name.replace(/\.docx$/, ""),
      key: selectedTreeItem.document.id,
      order: selectedTreeItem.document.order,
      hasChildren: selectedTreeItem.document.hasChildren,
      nodeType: "part",
      expanded: false,
      items: undefined,
      data: selectedTreeItem.document,
      hasReadPermissions: selectedTreeItem.readPermission,
    },
  ]);
  const [elementToUpdate, setElementToUpdate] = useState<DocumentRoot[]>([]);
  const [azureDefaultSite, setAzureDefaultSite] = useState("");
  const rootId = selectedTreeItem.document.id;
  const hasReadPermission = selectedTreeItem.readPermission;
  const userFunctions: UserFunction[] = useAppSelector(selectUserFunctions);
  const defaultServiceProvider: ServiceProvider = useAppSelector(selectServiceProvider);
  const seahorseDocumentLinked: SeahorseDocumentLinked[] = useAppSelector(selectSeahorseDocumentLinked);
  const allIxbrlSettings: IxbrlSetting[] = useAppSelector(selectIxbrlSettings);

  const iconManageStructure: IIconProps = { iconName: "Move" };
  useEffect(() => {
    let resAzureDefaultSite = fetchAzureDefaultSiteFromExternalConfig();
    resAzureDefaultSite.then((r) => {
      setAzureDefaultSite(r.azureDefaultSite);
    });
  }, []);
  useEffect(() => {
    fetchInitialDocumentPartTreeProps(selectedTreeItem, rootId, parents, setElementToUpdate).then((data) => {
      const documentRoots: DocumentRoot[] = [];
      data.forEach((value) => {
        let documentRoot = value as unknown as DocumentRoot;
        documentRoot.documentPart = value.data as DocumentPart;
        documentRoots.push(documentRoot);
      });
      setElementToUpdate((elementToUpdate) => [...elementToUpdate, ...documentRoots]);
      setTreeItems(data);
    });
    fetchAllDocumentPartStatus(rootId).then((value) => {
      setIsCertified(value);
    });
  }, [selectedTreeItem]);

  useEffect(() => {
    if (elementToUpdate.length > 0) {
      fetchDocumentPartStatus(rootId, getDocumentIds()).then((documentPartWithStatusReturned) => {
        const documentPartWithStatusReturnedBackup = documentPartWithStatusReturned || [];
        setDocumentPartWithStatus?.((prevState: DocumentPartWithStatus[]) => {
          const newElements = getNewElements(documentPartWithStatusReturnedBackup, prevState);
          return [...prevState, ...newElements];
        });
      });
    }
  }, [elementToUpdate]);

  function getDocumentIds() {
    const documentIds: string[] = [];
    for (let i = 0; i < elementToUpdate.length; i++) {
      documentIds.push(elementToUpdate[i].documentPart.id);
    }
    return documentIds;
  }

  function getNewElements(
    documentPartWithStatusReturnedBackup: DocumentPartWithStatus[],
    prevState: DocumentPartWithStatus[]
  ) {
    const newElements: DocumentPartWithStatus[] = [];
    const documentIds = new Set<String>();
    for (let i = 0; i < prevState.length; i++) {
      documentIds.add(prevState[i].documentPartId);
    }
    for (let i = 0; i < documentPartWithStatusReturnedBackup.length; i++) {
      if (!documentIds.has(documentPartWithStatusReturnedBackup[i].documentPartId)) {
        newElements.push(documentPartWithStatusReturnedBackup[i]);
      }
    }
    return newElements;
  }

  function onClick(page: "dragedrop" | "dbmapping") {
    if (page === "dragedrop") {
      updateTreeItems?.([], false, true);
      changePage("managestructure");
    } else {
      changePage("dbmapping");
    }
  }

  let notAuthorizedUser = isNotAuthorizedUser(UserFunctionEnum.MENU_OPEN_PANEL_MANAGE_TREE_STRUCTURE, userFunctions);
  let notAuthorizedUserForDbmapping = isNotAuthorizedUser(UserFunctionEnum.MENU_OPEN_PANEL_DBMAPPING, userFunctions);
  const [isCertified, setIsCertified] = useState(false);

  return (
    <>
      {isLibraryRibbon && (
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
          <>
            <ActionButton
              text={formatMessage("cof.opendocuments.outline.dbmapping")}
              onClick={() => onClick("dbmapping")}
              iconProps={{
                imageProps: {
                  src:
                    notAuthorizedUserForDbmapping || !hasReadPermission
                      ? "/assets/dbMappingDisabled.svg"
                      : "/assets/dbMapping.svg",
                  imageFit: ImageFit.contain,
                  width: "16px",
                  height: "16px",
                },
              }}
              style={{
                fontSize: "0.825rem",
                fontWeight: "bold",
                color: notAuthorizedUserForDbmapping || !hasReadPermission ? "var(--color-grey)" : "var(--color-blue)",
                cursor: notAuthorizedUserForDbmapping || !hasReadPermission ? "default" : "pointer",
              }}
              disabled={notAuthorizedUserForDbmapping || !hasReadPermission}
            />
            <ActionButton
              text={formatMessage("cof.opendocuments.outline.managetreestructure")}
              onClick={() => onClick("dragedrop")}
              iconProps={iconManageStructure}
              style={{
                fontSize: "0.825rem",
                fontWeight: "bold",
                color:
                  notAuthorizedUser || isCertified || !hasReadPermission ? "var(--color-grey)" : "var(--color-blue)",
                cursor: notAuthorizedUser || isCertified || !hasReadPermission ? "default" : "pointer",
              }}
              disabled={notAuthorizedUser || isCertified || !hasReadPermission}
              title={isCertified ? formatMessage("cof.base.managetreestructuretitle") : null}
            />
          </>
        </div>
      )}
      <DocumentPartTree
        isOutlineRibbon={isOutlineRibbon}
        isSourcesRibbon={isSourcesRibbon}
        isLibraryRibbon={isLibraryRibbon}
        isOpenOutlineRibbon={isOpenOutlineRibbon}
        selectedPartId={parents?.length > 0 ? parents[parents?.length - 1]?.idFiglio : rootId}
        isLinkExisting={isLinkExisting}
        rootParts={treeItems}
        idPartRoot={idPartRoot}
        getIconButtonMenuProps={(item, itemHierarchicalIndex, documentPartWithStatus: DocumentPartWithStatus[]) => {
          if (isSourcesRibbon || !item.hasReadPermissions) return null;
          if (isOutlineRibbon) return getIconButtonOutlineRibbon(item, itemHierarchicalIndex, documentPartWithStatus);
          return getIconButtonMenuProps(
            item,
            itemHierarchicalIndex,
            userFunctions,
            defaultServiceProvider,
            seahorseDocumentLinked,
            allIxbrlSettings,
            idPartRoot,
            rootId,
            onDeleteDocument,
            onExportDocument,
            documentPartWithStatus
          );
        }}
        onItemClick={onItemClick}
        isDocumentOutline={!isLinkExisting}
        updateTreeItems={updateTreeItems}
        openDocument={openDocumentById}
        parents={parents}
        azureDefaultSite={azureDefaultSite}
        setSelectedItem={onSetSelectedItems}
        elementToUpdate={setElementToUpdate}
        documentPartWithStatus={documentPartWithStatus}
        ignoreFetchPath={ignoreFetchPath}
        setDocumentPartWithStatus={setDocumentPartWithStatus}
      />
    </>
  );
};
